import { Component, lazy, Suspense, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Pill } from '@/components/Pill';

/**
 * The help button in the header. Only the button ships with the app: the
 * panel, its search and its answers load the first time it is opened, which
 * keeps them out of the main bundle (the PWA precaches files up to 2 MiB).
 *
 * A separate thing from the data assistant (the ✦ button), which reads the
 * workspace's records; this one only knows how the product works.
 */

/**
 * Whatever goes wrong inside the panel — a chunk that no longer exists after
 * a deploy, a bad saved conversation — costs the help panel, not the app.
 * Nothing above it catches errors.
 */
class PanelBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('Help panel failed', error);
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function HelpLauncher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Mounted from the first open on, so the conversation survives closing it.
  const [used, setUsed] = useState(false);
  // A failed load is retried on the next click: `lazy` remembers a rejected
  // import for good, so each attempt gets a fresh one.
  const [attempt, setAttempt] = useState(0);
  const HelpPanel = useMemo(() => lazy(() => import('./HelpPanel')), [attempt]);

  return (
    <>
      <Tooltip title={t('Help: how do I…?')}>
        <Pill
          iconOnly
          data-testid="help-launcher"
          aria-label={t('Help')}
          aria-haspopup="dialog"
          onClick={() => {
            setUsed(true);
            setOpen(true);
          }}
        >
          <HelpOutlineIcon />
        </Pill>
      </Tooltip>
      {used && (
        <PanelBoundary
          key={attempt}
          onError={() => {
            setOpen(false);
            setUsed(false);
            setAttempt((count) => count + 1);
          }}
        >
          <Suspense fallback={null}>
            <HelpPanel open={open} onClose={() => setOpen(false)} />
          </Suspense>
        </PanelBoundary>
      )}
    </>
  );
}
